<script lang="ts" context="module">
  import type { Load } from '@sveltejs/kit';
  import { getUserStore } from '$lib/stores/getUserStore';
  import type { UserLessonStore } from 'shared/src/user/userLessons/user.lesson.store';

  export const load: Load = async ({ session, params, fetch }) => {
    const clientUserStore = await getUserStore({ userWithJoins: session.user, fetch });

    const lesson = clientUserStore.lessonProgress.lessonsById[params.lessonId!];

    // If the lesson doesn't exist, 404!
    if (!lesson) {
      return {
        status: 404,
      };
    }

    // If the lesson isn't available, redirect to the base lesson
    // page to let them know
    if (!lesson.isVisible || !lesson.isAvailable) {
      return {
        status: 303,
        redirect: `/app/lessons/${params.lessonId}`,
      };
    }

    // If it's a checkpoint, go to the right page:
    if (lesson.type === 'checkpoint') {
      return {
        status: 303,
        redirect: `/app/checkpoints/${params.lessonId}`,
      };
    }

    // If the page id is a number, we can no longer handle it (this is
    // an angular link). Instead, redirect back to the lesson start:
    if (isNumeric(params.lessonPageId)) {
      return {
        status: 301,
        redirect: `/app/lessons/${params.lessonId}`,
      };
    }

    const lessonPage = lesson.pages.find((page) => page.id === params.lessonPageId!);

    // If the page doesn't exist, 404
    if (!lessonPage) {
      return {
        status: 404,
      };
    }

    // If it's not available, go back to the correct starting page:
    if (!lessonPage.isAvailable) {
      return {
        status: 303,
        redirect: `/app/lessons/${params.lessonId!}/${lesson.startingPage.id}`,
      };
    }

    // All is well, let's show them the page:
    return {
      props: {
        lesson,
        lessonPage,
        clientUserStore,
      },
    };
  };
</script>

<script lang="ts">
  import LessonContent from '$lib/app/lessons/lessonContent/LessonContent.svelte';
  import LessonPagesTableOfContents from '$lib/app/lessons/LessonPagesTableOfContents.svelte';
  import type { ClientUserStore } from 'shared/src';
  import { afterNavigate, beforeNavigate, goto } from '$app/navigation';
  import LessonPrevNextButtons from '$lib/app/lessons/LessonPrevNextButtons.svelte';
  import { autorun } from 'mobx';
  import type { IReactionDisposer } from 'mobx';
  import { onDestroy } from 'svelte';
  import {
    getLearningSessionTracker,
    LearningSessionTracker,
  } from '$lib/app/learningSessionTracker/learningSessionTracker';
  import { browser } from '$app/env';
  import type { UPracticeData } from 'shared/definitions/progress-data';
  import { titleCase } from 'shared/src/utils/titleCase';
  import PageTitle from '$lib/teach/resources/PageTitle.svelte';
  import { openModal } from '$lib/stores/modals.store';
  import { errorProvider } from '$lib/error-handling/errorProvider';
  import LessonTinyScreenTableOfContents from '$lib/app/lessons/LessonTinyScreenTableOfContents.svelte';
  import { isNumeric } from 'shared/src/utils/isNumeric';
  export let lesson: UserLessonStore;
  export let lessonPage: UserLessonStore['pages'][number];
  export let clientUserStore: ClientUserStore;

  // If they don't have access to this page, paywall them:
  if (!clientUserStore.hasPagesRemainingToday) {
    if (browser) {
      goto('/app/dashboard').then(() =>
        openModal(clientUserStore.type === 'anonymous' ? 'must-register' : 'must-subscribe')
      );
    } else {
      openModal(clientUserStore.type === 'anonymous' ? 'must-register' : 'must-subscribe');
    }
  }

  $: nextPage = lesson.getNextPage(lessonPage.id);
  $: previousPage = lesson.getPreviousPage(lessonPage.id);

  let destroyer: IReactionDisposer;
  let nextPageAvailable = false;
  let previousPageAvailable = false;
  let pageTitle: string;
  let startTime = Date.now();
  $: {
    if (destroyer) destroyer();
    destroyer = autorun(() => {
      nextPageAvailable = lessonPage.nextPageIsAvailable;
      previousPageAvailable = !!previousPage;
      pageTitle = titleCase(lesson.title) + ': ' + titleCase(lessonPage.title);
    });
    startTime = Date.now();
  }

  let learningSessionTracker: LearningSessionTracker;
  $: if (browser) {
    learningSessionTracker = getLearningSessionTracker(clientUserStore, lesson.id);
  }

  onDestroy(() => {
    if (destroyer) destroyer();
  });

  function goNextPage(contentCompleted: boolean, practiceData?: UPracticeData[]) {
    cancelAutoPageTransitionTimeout();
    const timeOnPage = Date.now() - startTime;
    try {
      clientUserStore.api.sendProgressData({
        lessonId: lesson.id,
        lessonPageId: lessonPage.id,
        practiceData,
        timeOnPage,
      });
    } catch (err) {
      errorProvider.error(err);
      console.error(err);
    }
    if (practiceData) {
      learningSessionTracker.addPracticeData(practiceData);
    } else {
      learningSessionTracker.addTime(timeOnPage);
    }
    if (contentCompleted) {
      // We update highest page completed:
      lesson.update({ highestPageCompleted: lesson.highestPageCompleted + 1 });
    }
    if (!nextPage) {
      // The lesson is over:
      goto('finish');
    } else {
      goto(nextPage.id);
    }
  }

  function goPreviousPage() {
    cancelAutoPageTransitionTimeout();
    if (!previousPage) {
      // We're on the first page
      return;
    } else {
      goto(previousPage.id);
    }
  }

  function contentError(err: any) {
    console.error(err);
  }

  let autoPageTransitionTimeout: NodeJS.Timeout | number | undefined = undefined;

  function cancelAutoPageTransitionTimeout() {
    if (autoPageTransitionTimeout) {
      clearTimeout(autoPageTransitionTimeout as NodeJS.Timeout);
    }
  }

  let disablePrev = false;
  let disableNext = false;
  beforeNavigate(() => {
    cancelAutoPageTransitionTimeout();
    disablePrev = true;
    disableNext = true;
  });
  afterNavigate(() => {
    disablePrev = false;
    disableNext = false;
  });

  onDestroy(cancelAutoPageTransitionTimeout);
</script>

{#key lessonPage.id}
  <PageTitle title={pageTitle} description={pageTitle + ' lesson on uTheory'} hideTitle />
  <LessonTinyScreenTableOfContents {lessonPage} {lesson} />
  <div class="flex card-margins">
    <LessonPagesTableOfContents class="hidden md:block w-1/4 pr-2 pt-4" {lesson} {lessonPage} />
    <div class="h-fit w-full md:w-3/4">
      <LessonContent
        {lessonPage}
        {clientUserStore}
        on:contentEnded={(data) => {
          cancelAutoPageTransitionTimeout();
          autoPageTransitionTimeout = setTimeout(() => {
            goNextPage(true, data.detail);
          }, 1000);
        }}
        on:contentError={contentError}
        on:timeSpent={(time) => {
          if (learningSessionTracker) learningSessionTracker.addTime(time.detail);
        }}
      />
      <LessonPrevNextButtons
        hidePrev={!previousPageAvailable}
        hideNext={!nextPageAvailable}
        {disablePrev}
        {disableNext}
        on:nextClicked={() => goNextPage(false)}
        on:prevClicked={goPreviousPage}
      />
    </div>
  </div>
{/key}
